/**
 * FilterModal - Modal to filter visible metadata types (folders)
 * 
 * Allows users to show/hide specific metadata types in the explorer
 * Keyboard shortcut: ⌘⇧F (Mac) / Ctrl+Shift+F (Windows)
 */

import { useEffect, useCallback } from 'react';
import { Filter, Check, X } from 'lucide-react';
import { useMetadataEditorStore } from '@/stores/metadataEditorStore';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { cn } from '@/lib/utils';

interface FilterModalProps {
  /** All available metadata types from GetMetadataTypes query */
  metadataTypes: string[];
  /** When true, org has no retrieval yet; show "Fetch metadata" CTA instead of "No metadata types available" */
  hasNoRetrieval?: boolean;
  /** Called when user clicks Fetch metadata (initial retrieval) */
  onFetchMetadata?: () => void | Promise<void>;
  /** When true, disable Fetch metadata button (fetch in progress) */
  isFetchingMetadata?: boolean;
}

export function FilterModal({ metadataTypes, hasNoRetrieval = false, onFetchMetadata, isFetchingMetadata = false }: FilterModalProps) {
  const isOpen = useMetadataEditorStore((state) => state.isFilterModalOpen);
  const setOpen = useMetadataEditorStore((state) => state.setFilterModalOpen);
  const visibleTypes = useMetadataEditorStore((state) => state.visibleMetadataTypes);
  const toggleType = useMetadataEditorStore((state) => state.toggleMetadataTypeFilter);
  const clearFilters = useMetadataEditorStore((state) => state.clearFilters);
  const selectAllFilters = useMetadataEditorStore((state) => state.selectAllFilters);

  const handleOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  // Keyboard shortcut: Cmd+Shift+F / Ctrl+Shift+F
  useKeyboardShortcuts({
    shortcuts: [
      {
        combo: [
          { key: 'f', modifiers: ['meta', 'shift'], preventDefault: true },
          { key: 'f', modifiers: ['ctrl', 'shift'], preventDefault: true },
        ],
        handler: handleOpen,
        description: 'Open filter modal',
      },
    ],
  });

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-filter-modal]')) {
        handleClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  const handleShowAll = () => {
    // Add all types to visible set
    selectAllFilters(metadataTypes);
  };

  const handleHideAll = () => {
    // Clear set = hide all
    clearFilters();
  };

  const isTypeSelected = (type: string) => {
    // null = show all, Set = show only those types
    if (visibleTypes === null) return true;
    return visibleTypes.has(type);
  };

  // null means all are visible, otherwise count the Set size
  const activeFilterCount = visibleTypes === null ? metadataTypes.length : visibleTypes.size;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Modal */}
      <div 
        data-filter-modal
        className="relative mx-4 w-full max-w-md bg-popover border border-border rounded-lg shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Filter Metadata Types</h2>
            {activeFilterCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary text-primary-foreground rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-muted rounded transition-colors"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        
        {/* Quick actions */}
        <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
          <button
            onClick={handleShowAll}
            className="text-xs text-primary hover:underline"
          >
            Show All
          </button>
          <span className="text-muted-foreground">•</span>
          <button
            onClick={handleHideAll}
            className="text-xs text-primary hover:underline"
          >
            Hide All
          </button>
        </div>
        
        {/* Types list */}
        <div className="max-h-80 overflow-auto py-2">
          {metadataTypes.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm">
              {hasNoRetrieval && onFetchMetadata ? (
                <>
                  <p className="text-muted-foreground mb-3">Fetch metadata to see types and get started.</p>
                  <button
                    type="button"
                    onClick={() => { onFetchMetadata(); handleClose(); }}
                    disabled={isFetchingMetadata}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {isFetchingMetadata ? 'Fetching...' : 'Fetch metadata'}
                  </button>
                </>
              ) : (
                <span className="text-muted-foreground">No metadata types available</span>
              )}
            </div>
          ) : (
            metadataTypes.map((type) => (
              <FilterItem
                key={type}
                type={type}
                isSelected={isTypeSelected(type)}
                onToggle={() => toggleType(type, metadataTypes)}
              />
            ))
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>
            {activeFilterCount === 0 
              ? 'All types hidden' 
              : `Showing ${activeFilterCount} of ${metadataTypes.length} types`
            }
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-background border border-border rounded text-[10px]">esc</kbd>
            {' '}to close
          </span>
        </div>
      </div>
    </div>
  );
}

interface FilterItemProps {
  type: string;
  isSelected: boolean;
  onToggle: () => void;
}

function FilterItem({ type, isSelected, onToggle }: FilterItemProps) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'w-full px-4 py-2 flex items-center gap-3 text-left transition-colors',
        'hover:bg-muted/50'
      )}
    >
      <div
        className={cn(
          'h-4 w-4 rounded border flex items-center justify-center transition-colors',
          isSelected
            ? 'bg-primary border-primary'
            : 'border-border bg-background'
        )}
      >
        {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
      </div>
      <span className="text-sm flex-1">{type}</span>
    </button>
  );
}

// Export a button component to trigger the modal
interface FilterButtonProps {
  activeCount: number;
  totalCount: number;
}

export function FilterButton({ activeCount, totalCount }: FilterButtonProps) {
  const setOpen = useMetadataEditorStore((state) => state.setFilterModalOpen);
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');
  const shortcutKey = isMac ? '⌘⇧F' : 'Ctrl+Shift+F';
  
  const isFiltering = activeCount > 0 && activeCount < totalCount;
  
  return (
    <button
      onClick={() => setOpen(true)}
      title={`Filter metadata types (${shortcutKey})`}
      className={cn(
        'p-1 rounded transition-colors',
        'hover:bg-muted',
        isFiltering && 'text-primary'
      )}
    >
      <Filter className="h-3.5 w-3.5" />
    </button>
  );
}
